import React, { useEffect, useState } from 'react';
import { superadminApi, API_URL } from '../api/api1';
import '../styles/pages/profile.css';

const BACKEND = API_URL;
const makeUrl = (path) => {
  if (!path || typeof path !== 'string') return '';
  if (/^(blob:|data:|https?:)/i.test(path)) return path;
  if (path.startsWith('/api/')) return `${BACKEND}${path}`;
  if (path.startsWith('/uploads')) return `${BACKEND}/api${path}`;
  return `${BACKEND}${path.startsWith('/') ? path : `/${path}`}`;
};

const ICONS = {
  person: <><circle cx="12" cy="8" r="3" /><path d="M5 20a7 7 0 0 1 14 0" /></>,
  mail: <><path d="M4 6h16v12H4z" /><path d="m4 7 8 6 8-6" /></>,
  key: <><circle cx="8.2" cy="12" r="3.2" /><path d="M11.4 12H21m-3 0v3m-3-3v2.5" /></>,
  lock: <><rect x="4.5" y="10" width="15" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>,
  shield: <><path d="M12 3 19 6v5c0 4.5-3 7.5-7 10-4-2.5-7-5.5-7-10V6l7-3Z" /><path d="m9 12 2 2 4-4" /></>,
  trash: <><path d="M4 7h16M9 7V5h6v2m-8 0 1 13h8l1-13" /><path d="M10 11v5m4-5v5" /></>,
};

const EMPTY_FORM = {
  username: '',
  email: '',
  mobile: '',
};

const readCachedForm = () => {
  try {
    const cachedUser = JSON.parse(localStorage.getItem('superadmin_user') || '{}');
    return {
      ...EMPTY_FORM,
      username: cachedUser.username || '',
      email: cachedUser.email || '',
      mobile: cachedUser.mobile || '',
    };
  } catch {
    return { ...EMPTY_FORM };
  }
};

const readCachedPhoto = () => {
  try {
    const cachedUser = JSON.parse(localStorage.getItem('superadmin_user') || '{}');
    return makeUrl(cachedUser.profile_photo || '');
  } catch {
    return '';
  }
};

const validateName = (value, label = 'Name') => {
  const text = String(value || '').trim();
  if (!text) return `${label} is required`;
  if (/\d/.test(text)) return `${label} should not contain numbers`;
  if (text.length < 2) return `${label} must be at least 2 characters`;
  return '';
};

const validateEmail = (value) => {
  const email = String(value || '').trim();
  if (!email) return 'Email address is required';
  if (!/^[^\s@]+@(?:[a-zA-Z0-9-]+\.)+com$/i.test(email)) {
    return 'Enter a valid email ending with .com (e.g. name@example.com)';
  }
  if (email.length > 150) return 'Email must not exceed 150 characters';
  return '';
};

const validatePhone = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length !== 10) return `Phone number must be exactly 10 digits (currently ${digits.length})`;
  if (!/^[6-9]\d{9}$/.test(digits)) return 'Phone number must start with 6, 7, 8, or 9';
  return '';
};

const preventDigits = (event) => {
  if (/\d/.test(event.key)) event.preventDefault();
};

function SectionTitle({ icon, children }) {
  return (
    <h3>
      <span className="profile-section-icon">
        <svg viewBox="0 0 24 24" aria-hidden="true">{ICONS[icon]}</svg>
      </span>
      <span className="profile-section-label">{children}</span>
    </h3>
  );
}

function Detail({ label, value, fallback = 'Not provided', className = '' }) {
  return (
    <div className={`detail-item${className ? ` ${className}` : ''}`}>
      <dt>{label}</dt>
      <dd>{value || <span className="detail-empty">{fallback}</span>}</dd>
    </div>
  );
}

function Field({
  id,
  label,
  value,
  display,
  editing,
  onChange,
  type = 'text',
  required = false,
  autoComplete,
  maxLength,
  placeholder,
  error,
  inputMode,
  onKeyDown,
  className = '',
}) {
  if (!editing) return <Detail label={label} value={display ?? value} className={className} />;

  const describedBy = error ? `${id}-error` : undefined;

  return (
    <div className={`detail-item${error ? ' has-error' : ''}`}>
      <dt>
        <label htmlFor={id}>
          {label}
          {required && <span className="field-required" aria-hidden="true">*</span>}
        </label>
      </dt>
      <dd>
        <input
          id={id}
          className="detail-input"
          type={type}
          value={value ?? ''}
          required={required}
          autoComplete={autoComplete}
          maxLength={maxLength}
          placeholder={placeholder}
          inputMode={inputMode}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={describedBy}
          onKeyDown={onKeyDown}
          onChange={e => onChange(e.target.value)}
        />
        {error && <small className="detail-error" id={`${id}-error`}>{error}</small>}
      </dd>
    </div>
  );
}

function DialogIcon({ type = 'lock' }) {
  return (
    <span className={`profile-dialog-icon${type === 'danger' ? ' is-danger' : ''}`} aria-hidden="true">
      <svg viewBox="0 0 24 24">{type === 'danger' ? ICONS.trash : ICONS.lock}</svg>
    </span>
  );
}

const getAvatarSize = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return 112;
  if (window.matchMedia('(max-width: 600px)').matches) return 76;
  if (window.matchMedia('(max-width: 900px)').matches) return 92;
  return 112;
};

const useAvatarSize = () => {
  const [size, setSize] = useState(getAvatarSize);
  useEffect(() => {
    const onResize = () => setSize(getAvatarSize());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return size;
};

export function SuperAdminProfilePage() {
  const avatarSize = useAvatarSize();
  const [user, setUser] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(readCachedForm);
  const [savedForm, setSavedForm] = useState(readCachedForm);
  const [savedPhoto, setSavedPhoto] = useState(readCachedPhoto);
  const [profilePhoto, setProfilePhoto] = useState(readCachedPhoto);
  const [pendingProfilePhoto, setPendingProfilePhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [removePhotoPending, setRemovePhotoPending] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);

  useEffect(() => () => {
    if (pendingProfilePhoto?.previewUrl) URL.revokeObjectURL(pendingProfilePhoto.previewUrl);
  }, [pendingProfilePhoto]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const response = await superadminApi.get('/api/superadmin/profile');
        const userData = response.data?.superadmin || {};
        const updatedForm = {
          ...EMPTY_FORM,
          username: userData.username || '',
          email: userData.email || '',
          mobile: userData.mobile || '',
        };
        setUser({ ...userData, ...updatedForm });
        const photo = makeUrl(userData.profile_photo || '');
        setProfilePhoto(photo);
        setSavedPhoto(photo);
        setForm(updatedForm);
        setSavedForm(updatedForm);
        localStorage.setItem('superadmin_user', JSON.stringify(userData));
      } catch (error) {
        console.error('Failed to fetch superadmin profile:', error);
        const fallback = readCachedForm();
        setUser(fallback);
        const photo = readCachedPhoto();
        setProfilePhoto(photo);
        setSavedPhoto(photo);
        setForm(fallback);
        setSavedForm(fallback);
      }
    };
    loadProfile();
  }, []);

  const createdLabel = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
    : '';
  const hasErrors = Object.values(formErrors).some(Boolean);

  const handleNameChange = (key, label) => (value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setFormErrors(prev => ({ ...prev, [key]: validateName(value, label) }));
  };

  const handleEmailChange = (value) => {
    setForm(prev => ({ ...prev, email: value }));
    setFormErrors(prev => ({ ...prev, email: validateEmail(value) }));
  };

  const handlePhoneChange = (value) => {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 10);
    setForm(prev => ({ ...prev, mobile: digits }));
    setFormErrors(prev => ({ ...prev, mobile: validatePhone(digits) }));
  };

  const startEditing = () => {
    setFormErrors({});
    setEditing(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const errors = {
      username: validateName(form.username, 'Username'),
      mobile: validatePhone(form.mobile),
    };
    setFormErrors(errors);
    const firstError = Object.values(errors).find(Boolean);
    if (firstError) {
      alert(firstError);
      return;
    }

    setSaving(true);
    try {
      const uploadImage = async (file) => {
        if (!file) return null;
        const data = new FormData();
        data.append('file', file);
        const response = await superadminApi.post('/api/superadmin/profile-photo', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (!response.data?.success || !response.data?.image_url) {
          throw new Error(response.data?.error || 'Image upload failed');
        }
        return response.data.image_url;
      };

      const profilePhotoUrl = await uploadImage(pendingProfilePhoto?.file);

      const payload = {
        username: form.username.trim(),
        mobile: form.mobile.trim(),
        ...(profilePhotoUrl ? { profile_photo: profilePhotoUrl } : {}),
      };

      const response = await superadminApi.put('/api/superadmin/profile', payload);
      const updatedUser = response.data?.superadmin;

      const photoRemoved = Boolean(removePhotoPending) && !profilePhotoUrl;
      if (photoRemoved) {
        try {
          await superadminApi.delete('/api/superadmin/profile-photo');
        } catch (error) {
          if (error.response?.status !== 404) throw error;
        }
      }

      const savedUser = { ...(user || {}), ...(updatedUser || {}) };
      if (profilePhotoUrl) savedUser.profile_photo = profilePhotoUrl;
      if (photoRemoved) savedUser.profile_photo = null;
      if (updatedUser || profilePhotoUrl || photoRemoved) {
        localStorage.setItem('superadmin_user', JSON.stringify(savedUser));
      }

      if (profilePhotoUrl) {
        const savedPhotoUrl = makeUrl(profilePhotoUrl);
        const cacheBuster = savedPhotoUrl.includes('?') ? '&' : '?';
        const nextPhoto = `${savedPhotoUrl}${cacheBuster}v=${Date.now()}`;
        setProfilePhoto(nextPhoto);
        setSavedPhoto(nextPhoto);
      } else if (photoRemoved) {
        setProfilePhoto('');
        setSavedPhoto('');
      }

      const nextForm = {
        ...form,
        username: updatedUser?.username ?? form.username,
        email: updatedUser?.email ?? form.email,
        mobile: updatedUser?.mobile ?? form.mobile,
      };
      setForm(nextForm);
      setSavedForm(nextForm);
      setPendingProfilePhoto(null);
      setRemovePhotoPending(false);
      setFormErrors({});
      setUser(prev => ({
        ...prev,
        ...updatedUser,
        ...nextForm,
        ...(profilePhotoUrl ? { profile_photo: profilePhotoUrl } : {}),
        ...(photoRemoved ? { profile_photo: null } : {}),
        created_at: prev?.created_at,
      }));
      setEditing(false);
      alert(photoRemoved ? 'Profile updated and photo removed' : 'Profile updated successfully');
    } catch (error) {
      console.error('Profile update failed:', error);
      alert(error.response?.data?.error || error.message || 'Could not save your profile');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    if (savedForm) setForm(savedForm);
    setPendingProfilePhoto(null);
    setRemovePhotoPending(false);
    setProfilePhoto(savedPhoto);
    setFormErrors({});
    setEditing(false);
  };

  const pickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file for the profile photo.');
      e.target.value = '';
      return;
    }
    const previewUrl = URL.createObjectURL(file);
    setPendingProfilePhoto({ file, previewUrl });
    setProfilePhoto(previewUrl);
    setRemovePhotoPending(false);
    e.target.value = '';
  };

  const closePasswordDialog = () => {
    if (passwordSaving) return;
    setPasswordOpen(false);
    setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError('');
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    const { oldPassword, newPassword, confirmPassword } = passwordForm;
    if (!oldPassword) return setPasswordError('Current password is required');
    if (!newPassword) return setPasswordError('New password is required');
    if (newPassword !== confirmPassword) return setPasswordError('New passwords do not match');
    if (newPassword.length < 6) return setPasswordError('Password must be at least 6 characters');

    setPasswordSaving(true);
    setPasswordError('');
    try {
      await superadminApi.post('/api/superadmin/change-password', {
        id: user?.id,
        old_password: oldPassword,
        new_password: newPassword,
      });
      alert('Password changed successfully');
      setPasswordOpen(false);
      setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      const message = error.response?.data?.error || error.response?.data?.message || 'Failed to change password';
      setPasswordError(message);
    } finally {
      setPasswordSaving(false);
    }
  };

  const hasCurrentPhoto = Boolean(pendingProfilePhoto || (!removePhotoPending && (user?.profile_photo || profilePhoto)));

  return (
    <div className={`profile-wrapper${editing ? ' is-editing' : ''}`}>
      <header className="profile-top-header">
        <div className="profile-heading">
          <span className="profile-heading-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="8.4" r="3.5" />
              <path d="M5.2 19.8a6.8 6.8 0 0 1 13.6 0" />
            </svg>
          </span>
          <div className="profile-heading-copy">
            <h1>{editing ? 'Edit profile' : 'My profile'}</h1>
            <p>{editing ? 'Update your details, then save your changes.' : 'Manage your super admin account information'}</p>
          </div>
        </div>
        <div className="profile-header-actions">
          {editing ? (
            <span className="editing-pill">
              <span className="editing-dot" aria-hidden="true" />
              Editing
            </span>
          ) : (
            <button type="button" className="edit-top-btn" onClick={startEditing} aria-label="Edit profile" title="Edit profile">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m15 5 4 4M4 20l4.5-1 10.8-10.8a2.1 2.1 0 0 0-3-3L5.5 16 4 20Z" /></svg>
              <span className="edit-top-label">Edit profile</span>
            </button>
          )}
        </div>
      </header>

      <form className="profile-form" onSubmit={handleSave}>
        <div className="profile-layout">
          <aside className="profile-card profile-identity" aria-label="Profile summary">
            <div className="profile-avatar-wrap">
              <div style={{ width: avatarSize, height: avatarSize, borderRadius: '50%', background: '#6366f1', color: '#fff', display: 'grid', placeItems: 'center', fontSize: avatarSize * 0.4, fontWeight: 700, margin: '0 auto' }}>
                {hasCurrentPhoto ? (
                  <img src={pendingProfilePhoto?.previewUrl || (removePhotoPending ? '' : profilePhoto)} alt="Profile" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                  <div>SA</div>
                )}
              </div>
            </div>
            {editing && (
              <div className="profile-photo-actions">
                <p className="profile-photo-hint">
                  {pendingProfilePhoto
                    ? 'New photo selected'
                    : removePhotoPending
                      ? 'Photo will be removed when you save'
                      : hasCurrentPhoto
                        ? 'Upload a new photo'
                        : 'Add a profile photo'}
                </p>
                <input id="profile-photo-input" type="file" accept="image/*" className="visually-hidden" onChange={pickImage} />
                <label className="btn primary" htmlFor="profile-photo-input" style={{ fontSize: 14, padding: '8px 16px' }}>
                  {hasCurrentPhoto ? 'Change photo' : 'Upload photo'}
                </label>
                {hasCurrentPhoto && !removePhotoPending && (
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => { setPendingProfilePhoto(null); setProfilePhoto(''); setRemovePhotoPending(true); }}
                    disabled={saving}
                    style={{ fontSize: 14, padding: '8px 16px', marginTop: 8 }}
                  >
                    Remove photo
                  </button>
                )}
              </div>
            )}
            <h2 className="profile-name">{form.username || 'Username not provided'}</h2>
            <p className="sub-email">{form.email || 'Email not provided'}</p>
            <div className="identity-badges">
              <span className="role-badge">
                <svg viewBox="0 0 24 24" aria-hidden="true">{ICONS.shield}</svg>
                Super Admin
              </span>
            </div>
            <dl className="identity-meta">
              <Detail label="Account created" value={createdLabel} fallback="Not available" />
            </dl>
            {!editing && (
              <button type="button" className="password-btn" onClick={() => setPasswordOpen(true)}>
                <svg viewBox="0 0 24 24" aria-hidden="true">{ICONS.key}</svg>
                Change password
              </button>
            )}
          </aside>

          <div className="profile-main">
            <section className="profile-card" aria-label="Account details">
              <div className="profile-section">
                <SectionTitle icon="person">Personal information</SectionTitle>
                <dl className="detail-grid">
                  <Field
                    id="profile-username"
                    label="Username"
                    editing={editing}
                    required
                    autoComplete="username"
                    maxLength={150}
                    value={form.username}
                    onChange={handleNameChange('username', 'Username')}
                    onKeyDown={preventDigits}
                    error={formErrors.username}
                  />
                  <Field
                    id="profile-email"
                    label="Email address"
                    type="email"
                    editing={false}
                    required
                    autoComplete="email"
                    maxLength={150}
                    value={form.email}
                    onChange={handleEmailChange}
                    error={formErrors.email}
                  />
                  <Field
                    id="profile-mobile"
                    label="Mobile number"
                    type="tel"
                    editing={editing}
                    autoComplete="tel"
                    maxLength={10}
                    inputMode="numeric"
                    placeholder="10-digit mobile number"
                    value={form.mobile}
                    onChange={handlePhoneChange}
                    error={formErrors.mobile}
                  />
                </dl>
              </div>
            </section>
          </div>
        </div>

        {editing && (
          <div className="profile-actionbar" role="region" aria-label="Save or cancel your changes">
            <p>{hasErrors ? 'Please fix the highlighted fields before saving.' : 'Changes aren\'t saved until you select Save changes.'}</p>
            <div className="profile-actionbar-buttons">
              <button type="button" className="btn ghost" onClick={cancelEdit} disabled={saving}>Cancel</button>
              <button type="submit" className="btn primary" disabled={saving || hasErrors}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        )}
      </form>

      {passwordOpen && (
        <div
          className="profile-dialog-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) closePasswordDialog(); }}
        >
          <div className="profile-dialog" role="dialog" aria-modal="true" aria-labelledby="profile-password-title">
            <div className="profile-dialog-head">
              <DialogIcon type="lock" />
              <div>
                <h2 id="profile-password-title">Change password</h2>
                <p>Use at least 6 characters. You will need the new password next time you sign in.</p>
              </div>
              <button type="button" className="profile-dialog-close" onClick={closePasswordDialog} aria-label="Close" disabled={passwordSaving}>×</button>
            </div>
            <form onSubmit={handlePasswordSave}>
              <div className="profile-dialog-body">
                <div className="profile-dialog-field">
                  <label htmlFor="profile-old-password">Current password</label>
                  <input
                    id="profile-old-password"
                    className="detail-input"
                    type="password"
                    autoComplete="current-password"
                    value={passwordForm.oldPassword}
                    onChange={e => { setPasswordForm(prev => ({ ...prev, oldPassword: e.target.value })); setPasswordError(''); }}
                    required
                  />
                </div>
                <div className="profile-dialog-field">
                  <label htmlFor="profile-new-password">New password</label>
                  <input
                    id="profile-new-password"
                    className="detail-input"
                    type="password"
                    autoComplete="new-password"
                    minLength={6}
                    value={passwordForm.newPassword}
                    onChange={e => { setPasswordForm(prev => ({ ...prev, newPassword: e.target.value })); setPasswordError(''); }}
                    required
                  />
                </div>
                <div className="profile-dialog-field">
                  <label htmlFor="profile-confirm-password">Confirm new password</label>
                  <input
                    id="profile-confirm-password"
                    className="detail-input"
                    type="password"
                    autoComplete="new-password"
                    minLength={6}
                    value={passwordForm.confirmPassword}
                    onChange={e => { setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value })); setPasswordError(''); }}
                    required
                  />
                </div>
                {passwordError && <p className="profile-dialog-error" role="alert">{passwordError}</p>}
              </div>
              <div className="profile-dialog-actions">
                <button type="button" className="btn ghost" onClick={closePasswordDialog} disabled={passwordSaving}>Cancel</button>
                <button type="submit" className="btn primary" disabled={passwordSaving}>
                  {passwordSaving ? 'Updating…' : 'Update password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export const SuperAdminProfile = SuperAdminProfilePage;
export default SuperAdminProfilePage;
